import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { MainLayout } from '../components/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  TrendingUp, 
  Zap, 
  Droplets, 
  Flame,
  Trash2,
  ArrowRight,
  User,
  Mail,
  Inbox,
  Upload,
  Globe,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

export const StatsBoard = () => {
  const navigate = useNavigate();
  const currentAccountStr = localStorage.getItem('current_account');
  const currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    processing: 0,
    failed: 0,
    pendingReview: 0,
    approved: 0,
    byCommodity: {},
    recentActivity: []
  });
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('90'); // default 3 months

  useEffect(() => {
    if (!currentAccount) {
      const timer = setTimeout(() => {
        const freshAccount = localStorage.getItem('current_account');
        if (!freshAccount) {
          toast.error('No account selected');
        } else {
          window.location.reload();
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
    fetchStats();
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount?.id, timeRange]);

  const fetchStats = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/documents`);
      
      const total = data.length;
      const completed = data.filter(d => d.status === 'completed').length;
      const processing = data.filter(d => d.status === 'processing').length;
      const failed = data.filter(d => d.status === 'failed').length;
      const pendingReview = data.filter(d => d.status === 'completed' && d.review_status !== 'approved').length;
      const approved = data.filter(d => d.review_status === 'approved').length;
      
      const byCommodity = data.reduce((acc, doc) => {
        const commodity = doc.commodity || 'Unknown';
        acc[commodity] = (acc[commodity] || 0) + 1;
        return acc;
      }, {});
      
      const recentActivity = data
        .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))
        .slice(0, 5);
      
      setStats({
        total,
        completed,
        processing,
        failed,
        pendingReview,
        approved,
        byCommodity,
        recentActivity
      });
    } catch (error) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/dashboard?days=${timeRange}`);
      setDashboardData(data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  const commodityIcons = {
    'Electricity': <Zap className="h-5 w-5 text-yellow-500" />,
    'Gas': <Flame className="h-5 w-5 text-orange-500" />,
    'Water': <Droplets className="h-5 w-5 text-blue-500" />,
    'Waste': <Trash2 className="h-5 w-5 text-green-500" />,
    'Sewer': <Droplets className="h-5 w-5 text-purple-500" />,
    'Heating Oil': <Flame className="h-5 w-5 text-red-500" />,
  };

  // Doughnut chart data for bill sources
  const sourceChartData = dashboardData?.source_breakdown ? [
    { name: 'Email', value: dashboardData.source_breakdown.email || 0, color: '#3b82f6' },
    { name: 'API', value: dashboardData.source_breakdown.api || 0, color: '#8b5cf6' },
    { name: 'Upload', value: dashboardData.source_breakdown.upload || 0, color: '#10b981' },
  ].filter(item => item.value > 0) : [];

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show label for small slices

    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight="bold">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <Clock className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Time Range Filter */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Organization Dashboard</h2>
          <p className="text-sm text-muted-foreground">Bill processing and user activity metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="text-xs uppercase tracking-wide font-medium">Time Range</Label>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40 rounded-none" data-testid="time-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 3 Months</SelectItem>
              <SelectItem value="180">Last 6 Months</SelectItem>
              <SelectItem value="365">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="rounded-none" onClick={() => { fetchStats(); fetchDashboard(); }}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wide">Total Bills</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {dashboardData?.summary?.total_received || stats.total}
              </span>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wide">Pending Review</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-4xl font-black tracking-tighter text-yellow-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {stats.pendingReview}
              </span>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wide">Approved</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-4xl font-black tracking-tighter text-green-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {stats.approved}
              </span>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-none border-border">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wide">Processing / Failed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {dashboardData?.summary?.processing || stats.processing}
                </span>
                <span className="text-muted-foreground">/</span>
                <span className="text-2xl font-black tracking-tighter text-red-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {dashboardData?.summary?.failed || stats.failed}
                </span>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Bills by Source - Doughnut Chart */}
        <Card className="rounded-none border-border lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Inbox className="h-5 w-5" />
              Bills by Source
            </CardTitle>
            <CardDescription className="text-xs">
              Distribution of bills received via Email, API, and Upload
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sourceChartData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sourceChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      labelLine={false}
                      label={renderCustomizedLabel}
                    >
                      {sourceChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value} bills`, name]}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '0'
                      }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value, entry) => (
                        <span className="text-sm">{value}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <p className="text-sm text-muted-foreground">No bills in selected period</p>
              </div>
            )}
            {/* Source breakdown numbers */}
            {dashboardData?.source_breakdown && (
              <div className="mt-4 grid grid-cols-3 gap-2 text-center border-t border-border pt-4">
                <div>
                  <div className="flex items-center justify-center gap-1 text-blue-500 mb-1">
                    <Mail className="h-4 w-4" />
                  </div>
                  <p className="text-xl font-bold">{dashboardData.source_breakdown.email || 0}</p>
                  <p className="text-xs text-muted-foreground uppercase">Email</p>
                </div>
                <div>
                  <div className="flex items-center justify-center gap-1 text-purple-500 mb-1">
                    <Globe className="h-4 w-4" />
                  </div>
                  <p className="text-xl font-bold">{dashboardData.source_breakdown.api || 0}</p>
                  <p className="text-xs text-muted-foreground uppercase">API</p>
                </div>
                <div>
                  <div className="flex items-center justify-center gap-1 text-green-500 mb-1">
                    <Upload className="h-4 w-4" />
                  </div>
                  <p className="text-xl font-bold">{dashboardData.source_breakdown.upload || 0}</p>
                  <p className="text-xs text-muted-foreground uppercase">Upload</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* User Activity Table */}
        <Card className="rounded-none border-border lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <User className="h-5 w-5" />
              User Activity
            </CardTitle>
            <CardDescription className="text-xs">
              Bills processed and approved by team members
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardData?.user_activity && dashboardData.user_activity.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="user-activity-table">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">User</th>
                      <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Bills Assigned</th>
                      <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Edits</th>
                      <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Approved</th>
                      <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Reparsed</th>
                      <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Total Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.user_activity.map((user, index) => (
                      <tr key={user.user_id || index} className="border-b border-border hover:bg-muted/30">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-4 w-4 text-primary" />
                            </div>
                            <span className="font-medium">{user.user_name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <Badge variant="outline" className="rounded-none">{user.bills_assigned || 0}</Badge>
                        </td>
                        <td className="p-3 text-center">
                          <span className="font-mono">{user.edits || 0}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="font-mono text-green-600">{user.approvals || 0}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="font-mono text-blue-600">{user.reprocessed || 0}</span>
                        </td>
                        <td className="p-3 text-center">
                          <Badge className="rounded-none">{user.total_actions}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 text-center">
                <User className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">No user activity in selected period</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bills by Commodity */}
        <Card className="rounded-none border-border lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Bills by Commodity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.byCommodity).map(([commodity, count]) => (
                <div key={commodity} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {commodityIcons[commodity] || <FileText className="h-5 w-5 text-muted-foreground" />}
                    <span className="text-sm font-medium">{commodity}</span>
                  </div>
                  <Badge variant="secondary" className="rounded-none">{count}</Badge>
                </div>
              ))}
              {Object.keys(stats.byCommodity).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No bills uploaded yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="rounded-none border-border lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold">Recent Activity</CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              className="rounded-none text-xs"
              onClick={() => navigate('/bill-jobs')}
            >
              View All <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recentActivity.map((doc) => (
                <div 
                  key={doc.id} 
                  className="flex items-center justify-between p-3 border border-border hover:bg-muted/25 transition-colors cursor-pointer"
                  onClick={() => doc.status === 'completed' && navigate(`/viewer/${doc.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium font-mono truncate max-w-[200px]">{doc.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(doc.uploaded_at), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.review_status === 'approved' ? (
                      <Badge className="rounded-none bg-blue-600">Approved</Badge>
                    ) : doc.status === 'completed' ? (
                      <Badge variant="outline" className="rounded-none">Review</Badge>
                    ) : doc.status === 'processing' ? (
                      <Badge className="rounded-none bg-yellow-500">Processing</Badge>
                    ) : doc.status === 'failed' ? (
                      <Badge variant="destructive" className="rounded-none">Failed</Badge>
                    ) : (
                      <Badge variant="secondary" className="rounded-none">Uploaded</Badge>
                    )}
                  </div>
                </div>
              ))}
              {stats.recentActivity.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">No recent activity</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <Card className="rounded-none border-border">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button 
                className="rounded-none" 
                onClick={() => navigate('/bill-jobs')}
              >
                <FileText className="h-4 w-4 mr-2" />
                Upload New Bills
              </Button>
              <Button 
                variant="outline" 
                className="rounded-none"
                onClick={() => navigate('/bulk-downloads')}
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Export Data
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default StatsBoard;
